import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tmdbAPI, watchlistAPI } from '../services/api';
import MediaCard from '../components/MediaCard';
import { HiOutlineFilm, HiOutlineVideoCamera, HiOutlineCollection } from 'react-icons/hi';

function Dashboard() {
  const { t } = useTranslation();
  const [trending, setTrending] = useState([]);
  const [stats, setStats] = useState({ movies: 0, series: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [trendingRes, watchlistRes] = await Promise.all([
        tmdbAPI.getTrending('all', 'week', 1),
        watchlistAPI.getAll(),
      ]);
      setTrending(trendingRes.data.results?.slice(0, 12) || []);

      const totalPages = trendingRes.data.total_pages || 1;
      setHasMore(totalPages > 1);
      setPage(1);

      const watchlist = watchlistRes.data;
      setStats({
        movies: watchlist.filter((i) => i.media_type === 'movie').length,
        series: watchlist.filter((i) => i.media_type === 'tv').length,
        total: watchlist.length,
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const trendingRes = await tmdbAPI.getTrending('all', 'week', nextPage);

      const newItems = trendingRes.data.results?.slice(0, 12) || [];
      setTrending((prev) => [...prev, ...newItems]);

      const totalPages = trendingRes.data.total_pages || 1;
      setHasMore(nextPage < totalPages);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t('dashboard.title')}</h1>
        <p>{t('dashboard.subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon purple">
            <HiOutlineFilm size={24} />
          </div>
          <h3>{stats.movies}</h3>
          <p>{t('dashboard.moviesInList')}</p>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon teal">
            <HiOutlineVideoCamera size={24} />
          </div>
          <h3>{stats.series}</h3>
          <p>{t('dashboard.seriesInList')}</p>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon pink">
            <HiOutlineCollection size={24} />
          </div>
          <h3>{stats.total}</h3>
          <p>{t('dashboard.totalTitles')}</p>
        </div>
      </div>

      {/* Trending */}
      <div className="card">
        <div className="card-header">
          <h2>{t('dashboard.trendingThisWeek')}</h2>
          {hasMore && trending.length > 0 && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="btn btn-primary"
              style={{ minWidth: '150px' }}
            >
              {loadingMore ? t('common.loading') : t('dashboard.loadMore')}
            </button>
          )}
        </div>

        <div className="card-content-scrollable">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="cards-grid">
              {trending.map((item) => (
                <MediaCard key={`${item.media_type}-${item.id}`} item={item} onUpdate={loadData} />
              ))}
            </div>
          )}

          {!loading && trending.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">
                <HiOutlineFilm size={32} />
              </div>
              <h3>{t('dashboard.noContentFound')}</h3>
              <p>{t('dashboard.configureApiKey')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
